// QuickBooks grouped sync (Batch 6) — select paid, unsynced receipts, sync
// them as ONE QuickBooks expense, and read the per-receipt history with group
// membership. Mirrors the legacy "Group Sync" modal: select 2+, review the
// combined total, optional vendor / memo override, one result per group.

import { useMemo, useState } from 'react';
import axios from 'axios';
import { Layers } from 'lucide-react';
import { Card } from '../../../../components/ui/Card/Card';
import { Button } from '../../../../components/ui/Button/Button';
import { Modal } from '../../../../components/ui/Modal/Modal';
import { Checkbox } from '../../../../components/form/Checkbox';
import { Input } from '../../../../components/form/Input';
import { Textarea } from '../../../../components/form/Textarea';
import { Pagination } from '../../../../components/ui/Pagination/Pagination';
import {
  useQbEligibleReceipts,
  useQbGroupSync,
  useQbSyncRecords,
} from './hooks/useQuickBooks';
import type { QbEligibleReceipt, QbGroupSyncResult } from '../../../../types/api';
import styles from './QbGroupSync.module.css';

function money(v: string | number): string {
  return `£${Number(v).toFixed(2)}`;
}

function fmtDate(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Minimum group size shown in the UI; the server is authoritative. */
const MIN_GROUP = 2;

interface GroupOutcome {
  ok: boolean;
  /** Receipt unique ids in the attempted group. */
  ids: string[];
  result?: QbGroupSyncResult;
  error?: string;
}

function errorInfo(err: unknown): { code: string | undefined; message: string } {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { code?: string; message?: string } | undefined;
    return {
      code: data?.code,
      message: data?.message ?? 'Group sync failed.',
    };
  }
  return { code: undefined, message: err instanceof Error ? err.message : 'Group sync failed.' };
}

export function QbGroupSync(): JSX.Element {
  const eligible = useQbEligibleReceipts(true);
  const groupSync = useQbGroupSync();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [vendorName, setVendorName] = useState('');
  const [memo, setMemo] = useState('');
  const [needsMixedConfirm, setNeedsMixedConfirm] = useState(false);
  const [outcomes, setOutcomes] = useState<GroupOutcome[]>([]);
  const [recordsPage, setRecordsPage] = useState(1);
  const records = useQbSyncRecords(recordsPage, 25);

  const receipts: QbEligibleReceipt[] = eligible.data?.receipts ?? [];
  const chosen = useMemo(() => receipts.filter((r) => selected.has(r.enc_id)), [receipts, selected]);
  const totalPence = chosen.reduce((sum, r) => sum + Math.round(Number(r.amount) * 100), 0);
  const modes = new Set(chosen.map((r) => (r.payment_mode_name ?? '').toLowerCase()));
  const mixedModes = chosen.length > 1 && modes.size > 1;
  const canGroup = chosen.length >= MIN_GROUP;

  function toggle(encId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(encId)) next.delete(encId);
      else next.add(encId);
      return next;
    });
  }

  function closeConfirm() {
    setConfirmOpen(false);
    setNeedsMixedConfirm(false);
  }

  async function submit(allowMixed: boolean) {
    const ids = chosen.map((r) => r.unique_id);
    try {
      const result = await groupSync.mutateAsync({
        receipt_enc_ids: chosen.map((r) => r.enc_id),
        ...(vendorName.trim() ? { vendor_name: vendorName.trim() } : {}),
        ...(memo.trim() ? { memo: memo.trim() } : {}),
        ...(allowMixed ? { allow_mixed_payment: true } : {}),
      });
      setOutcomes((prev) => [{ ok: true, ids, result }, ...prev]);
      setSelected(new Set());
      setVendorName('');
      setMemo('');
      closeConfirm();
    } catch (err) {
      const { code, message } = errorInfo(err);
      if (code === 'MIXED_PAYMENT_MODES') {
        // A human decision: show the confirmation instead of failing.
        setNeedsMixedConfirm(true);
        return;
      }
      setOutcomes((prev) => [{ ok: false, ids, error: message }, ...prev]);
      closeConfirm();
    }
  }

  const rec = records.data;
  const recPagination = rec?.pagination;

  return (
    <>
      <Card title="Sync as group">
        <p className={styles.muted}>
          Select two or more paid receipts to send to QuickBooks as one expense with a line per
          receipt.
        </p>
        {eligible.isLoading ? (
          <p className={styles.muted}>Loading eligible receipts…</p>
        ) : receipts.length === 0 ? (
          <p className={styles.muted}>No paid, unsynced receipts are waiting.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table} aria-label="Eligible receipts">
              <thead>
                <tr>
                  <th aria-label="Select" />
                  <th>Receipt</th>
                  <th>Customer</th>
                  <th>Vehicle</th>
                  <th>Date</th>
                  <th>Payment</th>
                  <th className={styles.num}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {receipts.map((r) => (
                  <tr key={r.enc_id}>
                    <td>
                      <Checkbox
                        label=""
                        aria-label={`Select ${r.unique_id}`}
                        checked={selected.has(r.enc_id)}
                        onChange={() => toggle(r.enc_id)}
                      />
                    </td>
                    <td>{r.unique_id}</td>
                    <td>{r.customer_name ?? '—'}</td>
                    <td>{r.car_registration ?? '—'}</td>
                    <td>{fmtDate(r.receipt_date)}</td>
                    <td>{r.payment_mode_name ?? '—'}</td>
                    <td className={styles.num}>{money(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className={styles.summaryRow}>
          <span>
            {chosen.length} selected · <strong>{money(totalPence / 100)}</strong>
          </span>
          {mixedModes ? (
            <span className={styles.warn} role="note">
              Mixed payment modes — QuickBooks takes one per expense.
            </span>
          ) : null}
          <Button
            variant="primary"
            icon={Layers}
            disabled={!canGroup}
            onClick={() => setConfirmOpen(true)}
          >
            Sync as group
          </Button>
        </div>
      </Card>

      {outcomes.length > 0 ? (
        <Card title="Group sync results">
          <ul className={styles.results}>
            {outcomes.map((o, i) => (
              <li key={i} className={o.ok ? styles.resultOk : styles.resultErr}>
                {o.ok && o.result ? (
                  <>
                    <strong>
                      {o.result.member_count} receipts → 1 QuickBooks expense (
                      {o.result.qb_entity_id})
                    </strong>
                    <span>
                      {' '}
                      · {money(o.result.total)}
                      {o.result.doc_number ? ` · DocNumber ${o.result.doc_number}` : ''}
                      {o.result.attachments_failed > 0
                        ? ` · ${o.result.attachments_failed} PDF attachment(s) failed`
                        : ''}
                    </span>
                  </>
                ) : (
                  <>
                    <strong>Not synced: {o.ids.join(', ')}</strong>
                    <span> — {o.error}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card title="Synced receipts" flush>
        <div className={styles.tableWrap}>
          <table className={styles.table} aria-label="Synced receipts">
            <thead>
              <tr>
                <th>Receipt</th>
                <th>Customer</th>
                <th className={styles.num}>Amount</th>
                <th>Status</th>
                <th>QuickBooks</th>
                <th>Group</th>
                <th>Synced</th>
              </tr>
            </thead>
            <tbody>
              {(rec?.records ?? []).length === 0 ? (
                <tr>
                  <td colSpan={7} className={styles.muted}>
                    Nothing has been synced yet.
                  </td>
                </tr>
              ) : (
                (rec?.records ?? []).map((r, i) => (
                  <tr key={`${r.receipt_enc_id ?? i}-${r.qb_entity_id ?? 'x'}`}>
                    <td>{r.unique_id ?? '—'}</td>
                    <td>{r.customer_name ?? '—'}</td>
                    <td className={styles.num}>{r.amount !== null ? money(r.amount) : '—'}</td>
                    <td>{r.status === 'synced' ? 'Synced' : `Failed${r.error_message ? `: ${r.error_message}` : ''}`}</td>
                    <td>
                      {r.qb_entity_id ?? '—'}
                      {r.doc_number ? ` (${r.doc_number})` : ''}
                    </td>
                    <td>
                      {r.is_group ? `Group of ${r.group_size} · #${r.qb_entity_id}` : '—'}
                    </td>
                    <td>{fmtDate(r.synced_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {recPagination && recPagination.totalPages > 1 ? (
          <div className={styles.paginationWrap}>
            <Pagination pagination={recPagination} onPageChange={setRecordsPage} />
          </div>
        ) : null}
      </Card>

      <Modal
        open={confirmOpen}
        onClose={closeConfirm}
        title="Sync as one QuickBooks expense"
        size="md"
        preventClose={groupSync.isPending}
        footer={
          <>
            <Button variant="secondary" onClick={closeConfirm} disabled={groupSync.isPending}>
              Cancel
            </Button>
            {needsMixedConfirm ? (
              <Button
                variant="primary"
                loading={groupSync.isPending}
                onClick={() => void submit(true)}
              >
                Use earliest receipt’s payment type
              </Button>
            ) : (
              <Button
                variant="primary"
                loading={groupSync.isPending}
                onClick={() => void submit(false)}
              >
                {`Sync ${chosen.length} receipts`}
              </Button>
            )}
          </>
        }
      >
        <p>
          <strong>{chosen.length}</strong> receipts totalling <strong>{money(totalPence / 100)}</strong>{' '}
          will become a single expense with one line per receipt. Each receipt’s PDF is attached to
          that expense when PDF attachment is enabled. This cannot be partly applied: either every
          receipt syncs or none does.
        </p>
        {needsMixedConfirm ? (
          <p className={styles.warn} role="alert">
            These receipts use different payment types or accounts. QuickBooks allows one per
            expense; the earliest receipt’s will be used for the whole group.
          </p>
        ) : null}
        <Input
          label="Vendor / payee (optional override)"
          value={vendorName}
          onChange={(e) => setVendorName(e.target.value)}
          placeholder="Leave blank to use the earliest receipt’s customer"
        />
        <Textarea
          aria-label="Memo"
          rows={3}
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="Optional memo. Blank = “GROUP SYNC — N transactions | Total | IDs | Dates”"
        />
      </Modal>
    </>
  );
}
